import React, { useState, useMemo } from 'react';
import { Search, Filter, Grid, List, Plus, Bot, ChevronDown } from 'lucide-react';
import { UserRole } from '@/types';
import AgentCard from '@/components/AgentCard';
import { fleetAgents } from '@/data/mockData';

interface FleetProps {
  currentRole: UserRole;
}

const Fleet: React.FC<FleetProps> = ({ currentRole }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTier, setSelectedTier] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [selectedMaturity, setSelectedMaturity] = useState<string>('All');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const accessibleAgents = useMemo(() => {
    if (currentRole === 'Operations') {
      return fleetAgents.filter(a => a.tier === 'Platform' || a.tier === 'Specialized');
    } else if (currentRole === 'Channel') {
      return fleetAgents.filter(a => a.tier === 'Platform' || a.tier === 'Channel');
    }
    return fleetAgents;
  }, [currentRole]);

  const filteredAgents = useMemo(() => {
    return accessibleAgents.filter(agent => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || 
        agent.name.toLowerCase().includes(q) ||
        agent.description.toLowerCase().includes(q) ||
        agent.tags?.some(tag => tag.toLowerCase().includes(q));
      const matchesTier = selectedTier === 'All' || agent.tier === selectedTier;
      const matchesStatus = selectedStatus === 'All' || agent.status === selectedStatus;
      const matchesMaturity = selectedMaturity === 'All' || agent.verdictMaturity === selectedMaturity;
      return matchesSearch && matchesTier && matchesStatus && matchesMaturity;
    });
  }, [accessibleAgents, searchQuery, selectedTier, selectedStatus, selectedMaturity]);

  const tiers = ['All', ...Array.from(new Set(accessibleAgents.map(a => a.tier)))];
  const statuses = ['All', ...Array.from(new Set(accessibleAgents.map(a => a.status)))];
  const maturities = ['All', ...Array.from(new Set(accessibleAgents.map(a => a.verdictMaturity)))];

  const hasFilters = selectedTier !== 'All' || selectedStatus !== 'All' || selectedMaturity !== 'All' || searchQuery;

  const clearFilters = () => {
    setSelectedTier('All');
    setSelectedStatus('All');
    setSelectedMaturity('All');
    setSearchQuery('');
  };

  const activeCount = accessibleAgents.filter(a => a.status === 'Active').length;
  const platformCount = accessibleAgents.filter(a => a.tier === 'Platform').length;
  const channelCount = accessibleAgents.filter(a => a.tier === 'Channel').length;

  return (
    <div className="page-transition active p-6 lg:p-8 space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-1">
            <h1 className="text-2xl font-bold text-text-heading">Fleet Registry</h1>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-gold-primary bg-gold-primary/10 border border-gold-primary/20">
              {accessibleAgents.length} AGENTS
            </span>
          </div>
          <p className="text-sm text-text-muted">
            AI agent registry · {currentRole} access
          </p>
        </div>
        
        {currentRole === 'IT/Dev' && (
          <button className="flex items-center space-x-1.5 px-4 py-2 bg-gold-primary text-navy-primary rounded-lg text-sm font-semibold hover:bg-gold-hover transition-colors">
            <Plus size={15} />
            <span>Add Agent</span>
          </button>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Active', value: activeCount, color: 'emerald' },
          { label: 'Platform', value: platformCount, color: 'blue' },
          { label: 'Channel', value: channelCount, color: 'purple' },
          { label: 'Total', value: accessibleAgents.length, color: 'gold' },
        ].map((stat) => (
          <div key={stat.label} className="bg-navy-card border border-navy-border rounded-xl p-4 flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${
              stat.color === 'emerald' ? 'bg-emerald-500/10' :
              stat.color === 'blue' ? 'bg-blue-500/10' :
              stat.color === 'purple' ? 'bg-purple-500/10' :
              'bg-gold-primary/10'
            }`}>
              <Bot size={18} className={`${
                stat.color === 'emerald' ? 'text-emerald-400' :
                stat.color === 'blue' ? 'text-blue-400' :
                stat.color === 'purple' ? 'text-purple-400' :
                'text-gold-primary'
              }`} />
            </div>
            <div>
              <p className={`text-xl font-bold ${
                stat.color === 'emerald' ? 'text-emerald-400' :
                stat.color === 'blue' ? 'text-blue-400' :
                stat.color === 'purple' ? 'text-purple-400' :
                'text-gold-primary'
              }`}>{stat.value}</p>
              <p className="text-[11px] text-text-muted">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="bg-navy-card border border-navy-border rounded-xl p-5">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md w-full">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search agents by name, description, or tag…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-4 bg-navy-primary border border-navy-border rounded-lg text-sm 
                         text-text-primary placeholder-text-muted 
                         focus:outline-none focus:ring-2 focus:ring-gold-primary/50 focus:border-gold-primary/50 transition-all"
            />
          </div>

          <div className="flex items-center space-x-3">
            {/* Filters */}
            <div className="flex items-center space-x-2">
              <Filter size={14} className="text-text-muted" />
              
              {[
                { value: selectedTier, setter: setSelectedTier, options: tiers, prefix: 'Tier' },
                { value: selectedStatus, setter: setSelectedStatus, options: statuses, prefix: 'Status' },
                { value: selectedMaturity, setter: setSelectedMaturity, options: maturities, prefix: 'Maturity' },
              ].map(({ value, setter, options, prefix }) => (
                <div key={prefix} className="relative">
                  <select
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    className="appearance-none px-3 py-1.5 pr-7 bg-navy-primary border border-navy-border rounded-lg text-xs text-text-primary cursor-pointer hover:border-navy-border/80 transition-colors"
                  >
                    {options.map(opt => (
                      <option key={opt} value={opt}>{prefix}: {opt}</option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                </div>
              ))}

              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="px-2.5 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-navy-primary border border-navy-border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 transition-colors ${
                  viewMode === 'grid' ? 'bg-gold-primary text-navy-primary' : 'text-text-muted hover:text-text-heading'
                }`}
              >
                <Grid size={15} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 transition-colors ${
                  viewMode === 'list' ? 'bg-gold-primary text-navy-primary' : 'text-text-muted hover:text-text-heading'
                }`}
              >
                <List size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <p className="text-xs text-text-muted">
        Showing {filteredAgents.length} of {accessibleAgents.length} agents
      </p>

      {filteredAgents.length === 0 ? (
        <div className="text-center py-16">
          <Bot size={48} className="mx-auto text-text-muted/30 mb-4" />
          <p className="text-text-muted mb-3">No agents match your criteria</p>
          <button onClick={clearFilters} className="text-sm text-gold-primary hover:text-gold-hover transition-colors">
            Clear all filters
          </button>
        </div>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5'
            : 'space-y-3'
        }>
          {filteredAgents.map(agent => (
            <AgentCard key={agent.id} {...agent} />
          ))}
        </div>
      )}

      {/* Role notices */}
      {currentRole === 'Channel' && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
          <p className="text-xs text-amber-400">
            <strong>Channel View:</strong> Showing Platform (read-only) and Channel agents. Contact IT for Platform modifications.
          </p>
        </div>
      )}
    </div>
  );
};

export default Fleet;
